// result-verifier-profile-tamper-checks.ts
//
// Helpers that keep `.avatar/verification-profile.json` — the rulebook a
// worker is verified against — out of the worker's reach. Split out of
// result-verifier.ts (plan 20260924-0205, 200-line budget); result-verifier.ts
// still decides WHEN each runs (step 5b on the inventory diff; a before/after
// snapshot around the recipe run for audit F3), because the timing is the point.
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { VERIFICATION_PROFILE_PATH } from "./controller-owned-paths.js";

/**
 * Current bytes of the verification profile, or `undefined` when absent.
 * Audit F3: compared before/after the recipe run so a mid-attempt rewrite is
 * caught even though the inventory diff was taken earlier.
 */
export async function readVerificationProfileBytes(repoDir: string): Promise<string | undefined> {
  try {
    return await readFile(join(repoDir, VERIFICATION_PROFILE_PATH), "utf8");
  } catch {
    return undefined;
  }
}

/**
 * Step 5b. The verification profile is a controller-owned path the scope
 * exclusion deliberately skips — but skipping it silently is only right for
 * the path the exclusion was BUILT for. Measured while writing this:
 * `.claude/state/autopilot-binding.json` appears in the diff of EVERY
 * attempt, because the controller itself writes a fresh binding each time
 * (Bug E is precisely about not reading that as a worker's scope
 * violation). Rejecting on it would block every mission — a first draft of
 * this check did exactly that and turned the suite red, which is how the
 * distinction got measured rather than assumed.
 *
 * `.avatar/verification-profile.json` is the opposite case: no controller
 * step writes it mid-attempt, and `checkProposedScope`
 * (proposal-applier.ts) is supposed to make claiming that scope impossible.
 * So an edit reaching here means the proposal boundary was bypassed by a
 * route nobody anticipated, and continuing would verify this attempt
 * against recipes the worker may have just written for itself.
 */
export function profileEditedInDiffReason(excludedPaths: readonly string[]): string | undefined {
  if (!excludedPaths.includes(VERIFICATION_PROFILE_PATH)) return undefined;
  return (
    `controller_owned_path_modified: ${VERIFICATION_PROFILE_PATH} — ` +
    "worker không được sửa danh sách recipe mà chính nó bị kiểm bằng."
  );
}

/**
 * Audit F3: the profile changed WHILE the recipes ran. Checked by the caller
 * before it returns any evidence reason: if a recipe rewrote the rulebook,
 * every verdict computed from that run is suspect — including a "pass"
 * produced by a recipe the worker just wrote for itself.
 */
export function profileChangedDuringRecipesReason(
  before: string | undefined,
  after: string | undefined,
): string | undefined {
  if (before === after) return undefined;
  return (
    `controller_owned_path_modified: ${VERIFICATION_PROFILE_PATH} đổi TRONG lúc chạy recipe — ` +
    "worker không được sửa danh sách recipe mà chính nó bị kiểm bằng."
  );
}
